import React, { useMemo, useState } from "react";
import type {
  RelatorioTemposEntregaPeriodo,
  RelatorioTemposSetorEntrega,
} from "../../../services/controleProducaoService";

type Recorte = "semana" | "quinzena" | "mes";

const RECORTES: { id: Recorte; label: string }[] = [
  { id: "semana", label: "Semana" },
  { id: "quinzena", label: "Quinzena" },
  { id: "mes", label: "Mês" },
];

function corPercentual(pct: number): string {
  if (pct >= 90) return "bg-emerald-600";
  if (pct >= 70) return "bg-amber-500";
  return "bg-rose-600";
}

function PeriodoLinhas({
  setores,
  recorte,
}: {
  setores: RelatorioTemposSetorEntrega[];
  recorte: Recorte;
}) {
  const grupos = useMemo(() => {
    const map = new Map<
      string,
      {
        periodoKey: string;
        periodoLabel: string;
        linhas: Array<{
          setorId: number;
          setorNome: string;
          bucket: RelatorioTemposEntregaPeriodo;
        }>;
      }
    >();
    for (const setor of setores) {
      const buckets =
        recorte === "semana"
          ? setor.porSemana
          : recorte === "quinzena"
            ? setor.porQuinzena
            : setor.porMes;
      for (const bucket of buckets) {
        const atual = map.get(bucket.periodoKey) ?? {
          periodoKey: bucket.periodoKey,
          periodoLabel: bucket.periodoLabel,
          linhas: [],
        };
        atual.linhas.push({
          setorId: setor.setorId,
          setorNome: setor.setorNome,
          bucket,
        });
        map.set(bucket.periodoKey, atual);
      }
    }
    return Array.from(map.values())
      .sort((a, b) => a.periodoKey.localeCompare(b.periodoKey))
      .map((grupo) => ({
        ...grupo,
        linhas: grupo.linhas
          .slice()
          .sort((a, b) => a.setorNome.localeCompare(b.setorNome, "pt-BR")),
      }));
  }, [setores, recorte]);

  if (grupos.length === 0) {
    return (
      <p className="px-4 py-6 text-center text-sm text-neutral-500">
        Nenhum dia de produção no recorte filtrado.
      </p>
    );
  }

  return (
    <div className="divide-y divide-neutral-200">
      {grupos.map((grupo) => (
        <div key={grupo.periodoKey} className="px-4 py-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {grupo.periodoLabel}
          </p>
          <ul className="space-y-2">
            {grupo.linhas.map((linha) => (
              <li key={`${grupo.periodoKey}-${linha.setorId}`}>
                <div className="mb-1 flex min-w-0 items-baseline justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate font-medium text-neutral-800">
                    {linha.setorNome}
                  </span>
                  <span className="shrink-0 text-right tabular-nums text-neutral-600">
                    {linha.bucket.itensEntregues}/{linha.bucket.itensEsperados}{" "}
                    itens ·{" "}
                    <span className="font-semibold text-neutral-900">
                      {linha.bucket.percentualFormatado}
                    </span>
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-neutral-100">
                  <div
                    className={`h-full rounded-full ${corPercentual(linha.bucket.percentual)}`}
                    style={{
                      width: `${Math.min(100, linha.bucket.percentual)}%`,
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function EntregaSetorSection({
  setores,
}: {
  setores: RelatorioTemposSetorEntrega[];
}) {
  const [recorte, setRecorte] = useState<Recorte>("semana");

  if (setores.length === 0) return null;

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-neutral-800">
          Entrega por setor
        </h2>
        <p className="mt-1 text-xs text-neutral-500">
          Percentual de itens do setor lançados na ficha (produziu ou não
          produzido pela unidade), nos dias de produção do período filtrado.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {setores.map((setor) => (
          <article
            key={setor.setorId}
            className="rounded-lg border border-neutral-200 bg-white p-4"
          >
            <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              {setor.setorNome}
            </p>
            <p className="mt-1 text-3xl font-bold tabular-nums text-neutral-900">
              {setor.periodo.percentualFormatado}
            </p>
            <p className="mt-1 text-xs text-neutral-500">
              {setor.periodo.itensEntregues} de {setor.periodo.itensEsperados}{" "}
              itens no período
            </p>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-neutral-100">
              <div
                className={`h-full rounded-full ${corPercentual(setor.periodo.percentual)}`}
                style={{
                  width: `${Math.min(100, setor.periodo.percentual)}%`,
                }}
              />
            </div>
          </article>
        ))}
      </div>
      <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
        <div className="flex flex-wrap gap-2 border-b border-neutral-200 px-4 py-3">
          {RECORTES.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setRecorte(item.id)}
              className={`rounded-full px-3 py-1 text-sm font-semibold ${
                recorte === item.id
                  ? "bg-primary text-white"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        <PeriodoLinhas setores={setores} recorte={recorte} />
      </div>
    </section>
  );
}
